import React, { useContext, useEffect, useState } from "react";
import { ApplicationHeader, ApplicationLayout, NotFoundState, NotificationsContainer, Sidebar, Spinner } from "@rio-cloud/rio-uikit";
import Notification from "@rio-cloud/rio-uikit/Notification";
import { SidebarDetail } from "@components";
import { useAppSelector, store } from "@store";
import { fetchAddressWithLatLong, fetchTripPublic } from "src/services/routes";
import { AddressSearch } from "src/types/routes";
import { IOnGoingTrip, IStopEta, ITripStopItem } from "@types";
import { formatStopArrivalUtcToLocal, mountPolyline, renderLocation } from "@utils";
import { Provider } from "react-redux";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";

import RioMap from "src/components/Map";
import { useIsMobile, usePublicRoutePolyline } from "@hooks";
import { HeaderContent } from "./components/HeaderContent";

const MOBILE_MAP_HEIGHT = "45vh";

const mapPublicStops = (rawStops?: any[], stopsEta?: IStopEta[]): ITripStopItem[] => {
    const etaByRouteStopId = new Map((stopsEta ?? []).map((eta) => [eta.routeStopId, eta]));

    return (rawStops ?? [])
        .filter((stop) => stop?.type?.toUpperCase() !== 'UNPLANNED')
        .map((stop) => {
            const stopEta = etaByRouteStopId.get(stop?.id);

            return {
                note: stop?.stop?.note ?? stop?.stop?.name ?? 'Parada programada',
                label: stop?.stop?.position?.address?.label ?? '',
                order: stop?.stopQueueOrder,
                routeStopId: stop?.id,
                estimatedArrival: formatStopArrivalUtcToLocal(stopEta?.estimatedArrival),
                actualArrival: formatStopArrivalUtcToLocal(stopEta?.actualArrival),
                stopStatus: stopEta?.status,
            };
        })
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const RouteView = () => {
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<IOnGoingTrip | null>();
    const [currencyAddress, setCurrencyAddress] = useState<AddressSearch>();
    const [error, setError] = useState(false);
    const [originLabel, setOriginLabel] = useState('');
    const [destinyLabel, setDestinyLabel] = useState('');
    const [routeStops, setRouteStops] = useState<ITripStopItem[]>([]);
    const uuid = new URLSearchParams(document.location.search).get('link');
    const isMobile = useIsMobile();
    const { position, segments, markers, markerNow, currencySegments, boundingBox, locationNow, handleRoutes, send } = usePublicRoutePolyline();

    const applyRouteStops = (response: any) => {
        setOriginLabel(response?.originLocation?.address ?? '');
        setDestinyLabel(response?.destinyLocation?.address ?? '');
        setRouteStops(mapPublicStops(response?.stops, response?.stopsEta));
    };

    const fetchRouteDetails = async () => {
        try {
            const response = await fetchTripPublic(uuid as string);
            setSelected({
                actualProgress: response.progressTrip.toString(),
                assetId: response.vehicle.assetId,
                assetName: response.vehicle.assetName,
                dateStarted: response.scheduleTripDate,
                destinyName: response.destinyLocation.address,
                distance: response.remainingDistance.toString(),
                driverId: '',
                driverName: '',
                eta: response.dinamicETA,
                polyline: response.currentPolyline,
                routeId: response.polylinePlanned.routes[0].id,
                routeStatusNow: response.routeStatus,
                tripId: '',
                tripStatusNow: response.tripStatus,
                latLongActually: response.latLongActually,
                arrival: response.expectateArriveAt,
                departure: response.startDateTime,
                profileId: response.profileId,
                duration: response.duration,
                scheduledDateTime: response.scheduleTripDate,
                distanceTraveled: response.distanceTraveled,
                remainingDistance: response.remainingDistance,
            });
            applyRouteStops(response);
            setError(false);
            setLoading(false);
            handleRoutes(response.polylinePlanned.routes as any, response.stops);

            if (!response?.positions) {
                return;
            }
            send(response?.positions?.reverse())
        } catch (error) {
            console.log(error)
            setLoading(false);
            setError(true);
        }
    };

    const updatedRoute = async () => {
        try {
            const response = await fetchTripPublic(uuid as string);

            setSelected({
                actualProgress: response.progressTrip?.toString(),
                assetId: response.vehicle.assetId,
                assetName: response.vehicle.assetName,
                dateStarted: response.scheduleTripDate,
                destinyName: response.destinyLocation.address,
                distance: response.remainingDistance?.toString(),
                driverId: '',
                driverName: '',
                eta: response.dinamicETA,
                polyline: response.currentPolyline,
                routeId: response.polylinePlanned.routes?.[0]?.id,
                routeStatusNow: response.routeStatus,
                tripId: '',
                tripStatusNow: response.tripStatus,
                latLongActually: response.latLongActually,
                arrival: response.dinamicETA,
                departure: response.startDateTime,
                profileId: response.profileId,
                duration: response.duration,
                distanceTraveled: response.distanceTraveled,
                scheduledDateTime: response.scheduleTripDate,
                remainingDistance: response.remainingDistance,
            });

            applyRouteStops(response);

            if (!response.positions) {
                return;
            }

            send(response?.positions?.reverse())
        } catch (error) {
            console.log(error)
        }
    };

    const handleSearchAddress = async () => {
        const response = await fetchAddressWithLatLong(selected?.latLongActually?.lat, selected?.latLongActually?.lng)
        setCurrencyAddress(response[0])
    }

    const handleCopy = () => {
        const url = window.location.href; // Obtém a URL atual
        navigator.clipboard.writeText(url)
            .then(() => Notification.info("URL copiada para a área de transferência!"))
            .catch(err => console.error("Erro ao copiar: ", err));
    }

    useEffect(() => {
        fetchRouteDetails();

        const interval = setInterval(() => {
            updatedRoute();
        }, 30000);

        return () => {
            clearInterval(interval);
        };
    }, [uuid])

    useEffect(() => {
        if (selected?.latLongActually) {
            handleSearchAddress()
        }
    }, [selected?.latLongActually]);

    const detailsContent = !error ? (
        <SidebarDetail
            currencyLocation={locationNow}
            handleCopy={handleCopy}
            currencyAddress={currencyAddress?.title}
            detail={selected}
            originLabel={originLabel}
            destinyLabel={destinyLabel}
            stops={routeStops}
            type='PUBLIC'
        />
    ) : (
        <div className="width-100pct height-100pct display-flex align-items-center justify-content-center">
            <NotFoundState
                headline="Página indisponível"
                message="Volte e tente novamente."
                fullWidth
                outerClassName="border-none"
            />
        </div>
    );

    const mapContent = (
        <RioMap
            zoom={12}
            position={position}
            boundingBox={boundingBox}
            apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
            segments={
                segments.length ? [{ points: segments, alternative: false }] : []
            }
            currencySegments={
                currencySegments.length ? [{ points: currencySegments, alternative: false }] : []
            }
            markers={markerNow?.position?.lat ? [...markers, markerNow] : markers}
            useClusters={true}
        />
    );

    const desktopLayout = (
        <>
            <ApplicationLayout.Sidebar className="right height-100pct">
                <Sidebar
                    title="Veículo"
                    headerClassName="padding-left-20"
                    bodyClassName="padding-x-20"
                    position={Sidebar.RIGHT}
                    disableClose
                    width={500}
                >
                    {detailsContent}
                </Sidebar>
            </ApplicationLayout.Sidebar>

            <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
                {mapContent}
            </ApplicationLayout.Body>
        </>
    );

    const mobileLayout = (
        <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct overflow-y-auto">
            <div className="width-100pct" style={{ height: MOBILE_MAP_HEIGHT }}>
                {mapContent}
            </div>
            <div className="bg-white padding-x-20 padding-bottom-20">
                <div className="text-size-h4 text-medium padding-y-15">Veículo</div>
                {detailsContent}
            </div>
        </ApplicationLayout.Body>
    );

    return (
        <ApplicationLayout>
            <ApplicationLayout.Header>
                <HeaderContent />
            </ApplicationLayout.Header>
            {!loading ? (
                <ApplicationLayout.Body innerClassName="padding-0" className="padding-0 margin-0">
                    <Provider store={store}>
                        <MapDrawingProvider>
                            {isMobile ? mobileLayout : desktopLayout}
                        </MapDrawingProvider>
                    </Provider>
                </ApplicationLayout.Body>
            ) : (
                <div className="width-100pct height-100vh bg-white display-flex align-items-center justify-content-center">
                    <Spinner isDoubleSized text='Carregando...' />
                </div>
            )}
        </ApplicationLayout>
    );
};

export default RouteView;